import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import HonorCodeModal from '../ChallengeWire/HonorCodeModal';
import { challengeCardBtn } from '../ChallengeWire/ChallengeCard';
import { accentSectionLabelClass } from '../ChallengeWire/challengeWireFormStyles';
import { honorCodeFirstName } from '../../constants/honorCode';
import { celebrateChallengeComplete } from '../../utils/celebrationConfetti';
import {
	aiTrainerHeadingClassName,
	aiTrainerPrimaryButtonClassName,
	aiTrainerSecondaryButtonClassName
} from './aiTrainerStyles';

function challengeKindForItem(item) {
	return item.challenge_type === 'friend' ? 'friend' : 'household';
}

function challengeFromItem(item) {
	if (!item?.challenge_id) return null;

	return {
		id: item.challenge_id,
		kind: challengeKindForItem(item),
		title: item.title,
		challenged_profile: item.challenged_profile,
		can_accept: item.can_accept,
		can_complete: item.can_complete,
		can_fail: item.can_fail
	};
}

function honorModalProps(challenge) {
	if (!challenge) return { title: 'Accept challenge' };
	if (challenge.kind === 'household' && challenge.challenged_profile?.name) {
		return {
			acceptingAsName: honorCodeFirstName(challenge.challenged_profile.name),
			challengeTitle: challenge.title
		};
	}
	return { title: `Accept: ${challenge.title}` };
}

const BRIEFING_INTRO_BY_ITEM_TYPE = {
	challenge_grace_window: 'Your challenge has ended — did you get it done?',
	challenge_deadline_today: 'Your challenge is due today.',
	challenge_deadline_urgent: 'Your challenge closes soon.',
	pending_challenge_stale: 'You have a challenge waiting for your response.',
	pending_acceptance: 'Accept or pass when you are ready.'
};

function briefingIntroText(briefing) {
	const items = Array.isArray(briefing?.items) ? briefing.items : [];

	if (briefing?.headline === "Today's priorities") {
		return "Here's what's on your list today.";
	}

	if (items.length === 1) {
		const intro = BRIEFING_INTRO_BY_ITEM_TYPE[items[0].type];
		if (intro) return intro;
	}

	return briefing?.message || '';
}

function BriefingItemActions({ item, onAcceptPending, onComplete, actionLoading }) {
	const challenge = challengeFromItem(item);

	if (!challenge || (!challenge.can_accept && !challenge.can_complete && !challenge.can_fail)) {
		return (
			<Link
				to="/challenge-wire"
				className={`inline-flex ${aiTrainerSecondaryButtonClassName} text-xs px-3 py-1.5`}
			>
				View on Challenge Wire
			</Link>
		);
	}

	return (
		<div className="flex flex-wrap gap-2">
			{challenge.can_accept && (
				<button
					type="button"
					disabled={actionLoading}
					onClick={() => onAcceptPending(challenge)}
					className={`${aiTrainerPrimaryButtonClassName} text-xs px-3 py-1.5`}
				>
					Accept
				</button>
			)}
			{(challenge.can_complete || challenge.can_fail) && (
				<button
					type="button"
					disabled={actionLoading || !challenge.can_complete}
					title={!challenge.can_complete ? 'Mark daily progress on Challenge Wire before completing.' : undefined}
					onClick={() => {
						if (!challenge.can_complete) return;
						onComplete(challenge)
							.then(() => celebrateChallengeComplete())
							.catch(() => {});
					}}
					className={`${challengeCardBtn.success} text-xs px-3 py-1.5${challenge.can_complete ? '' : ' cursor-not-allowed opacity-50'}`}
				>
					Mark complete
				</button>
			)}
			<Link
				to="/challenge-wire"
				className={`inline-flex ${aiTrainerSecondaryButtonClassName} text-xs px-3 py-1.5`}
			>
				View challenge
			</Link>
		</div>
	);
}

export default function AiTrainerBriefingBanner({
	briefing,
	onDismiss,
	onSnooze,
	onRunAction,
	actionLoading = false,
	variant = 'banner'
}) {
	const [honorChallenge, setHonorChallenge] = useState(null);

	if (!briefing) return null;

	const introText = briefingIntroText(briefing);

	function handleAcceptPending(challenge) {
		setHonorChallenge(challenge);
	}

	const isRail = variant === 'rail';
	const shellClassName = isRail
		? 'rounded-xl border border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-bg px-4 py-3'
		: 'rounded-2xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-dark-bg px-5 py-4';

	return (
		<>
			<div className={shellClassName}>
				<div className={`flex items-start justify-between gap-4 ${isRail ? 'flex-col' : ''}`}>
					<div className="min-w-0 space-y-3 w-full">
						{!isRail && <p className={accentSectionLabelClass}>{briefing.headline}</p>}
						{introText && (
							<p className={`text-base leading-relaxed ${aiTrainerHeadingClassName}`}>{introText}</p>
						)}
						{Array.isArray(briefing.items) && briefing.items.length > 0 && (
							<ul className="space-y-3">
								{briefing.items.map((item, index) => (
									<li
										key={`${item.type}-${item.challenge_id || index}`}
										className="rounded-xl border border-blue-100 dark:border-dark-border bg-white dark:bg-dark-card px-4 py-3"
									>
										<p className={`text-sm font-medium ${aiTrainerHeadingClassName}`}>{item.title}</p>
										<div className="mt-3">
											<BriefingItemActions
												item={item}
												onAcceptPending={handleAcceptPending}
												onComplete={(selected) => onRunAction(selected, 'complete')}
												actionLoading={actionLoading}
											/>
										</div>
									</li>
								))}
							</ul>
						)}
					</div>
					{onDismiss && (
						<div className={`flex shrink-0 gap-2 ${isRail ? 'w-full flex-row flex-wrap items-center' : 'flex-col items-end'}`}>
							{onSnooze && (
								<div className="flex flex-col gap-2">
									<button
										type="button"
										onClick={() => onSnooze(2)}
										className={`${aiTrainerSecondaryButtonClassName} text-xs px-3 py-1.5 whitespace-nowrap`}
									>
										Snooze 2h
									</button>
									<button
										type="button"
										onClick={() => onSnooze(null)}
										className={`${aiTrainerSecondaryButtonClassName} text-xs px-3 py-1.5 whitespace-nowrap`}
									>
										Until tomorrow
									</button>
								</div>
							)}
							<button
								type="button"
								onClick={onDismiss}
								className="rounded-lg px-2 py-1 text-sm font-medium text-blue-700 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-dark-card transition"
							>
								Dismiss
							</button>
						</div>
					)}
				</div>
			</div>

			<HonorCodeModal
				isOpen={Boolean(honorChallenge)}
				onClose={() => setHonorChallenge(null)}
				onConfirm={() =>
					onRunAction(honorChallenge, 'accept')
						.then(() => setHonorChallenge(null))
						.catch(() => {})
				}
				{...honorModalProps(honorChallenge)}
			/>
		</>
	);
}
